// reportMetrics.js - the board report's @figure metric catalog + pure resolver.
//
// SP6 lets a narrative contain `{{fig:KEY}}` tokens (see renderNarrative.jsx, a later SP6 task)
// that render a LIVE value pulled from the same figure endpoints the report's chart blocks
// already fetch (react-query cache via useBlockData - see useReportMetrics.js, no new network
// call). This file is the pure half: a catalog describing where each metric's raw value lives
// within a figure endpoint's response, and how to format it, plus resolveMetrics - the pure
// reducer from { endpoint: <unwrapped figure data> } to { key: {key,label,value} }.
//
// Endpoint shapes (source of truth: read directly from the Go structs + the FE block that
// already consumes each endpoint - backend_go/internal/boardfigures/{kpi,performance,forecast,
// riskbridge,pvm}.go and src/views/board-reports/blocks/{KPIStripBlock,PerformanceTableBlock,
// ForecastBandBlock,RiskBridgeBlock,VarianceWaterfallBlock}.jsx). `select(data)` below reads from
// the UNWRAPPED figure object (i.e. already past the envelope key) - useReportMetrics.js is
// responsible for unwrapping each endpoint's envelope before building figuresByEndpoint:
//
//   figures/kpis          -> { measures: [{key,value,display,delta,tone,spark}] }
//     (boardfigures.KPIStrip; measure keys are the fixed "month_spend"/"vs_budget"/"mom"/
//     "forecast_90d" - kpi.go's BuildKPIStrip)
//   figures/performance    -> { rows: [{metric,month,budget,variance,ytd,ytd_budget,fy_forecast}] }
//     (boardfigures.PerformanceTable; exactly 3 rows, metric = "Cloud"|"AI"|"Total" -
//     performance.go's BuildPerformance)
//   figures/forecast-band  -> { points: [{date,actual,forecast,lower,upper,budget,is_forecast}] }
//     (boardfigures.ForecastBand; oldest->newest, actual set on history days, forecast/lower/
//     upper set on forecast days - forecast.go's BuildForecastBand)
//   figures/risk-bridge    -> { base_label,base_total,legs:[{label,kind,delta}],upper_label,
//     upper_total,band_upper } (boardfigures.RiskBridge - riskbridge.go's BuildRiskBridge)
//   figures/pvm            -> { prev_label,prev_total,steps,current_label,current_total,
//     drivers:[{driver,volume,rate,delta,category}] } (cmd/api/board_figures_handlers.go's
//     PVMFigure; `drivers` is the FULL per-driver breakdown - boardfigures.DecomposePVM's
//     res.PerDriver, already sorted by |delta| desc - `steps` is the capped/bucketed chart-bar
//     chain and is NOT used here since it folds small drivers into "Other drivers"/"New"/etc.)
import { humanEUR, pct } from "../../cost-control/money";

// findMeasure - looks up one KPIMeasure by its fixed key from a figures/kpis response.
function findMeasure(kpis, key) {
  const measures = kpis && kpis.measures;
  if (!Array.isArray(measures)) return undefined;
  return measures.find((m) => m && m.key === key);
}

// findRow - looks up one PerfRow by its fixed "Cloud"/"AI"/"Total" metric label from a
// figures/performance response.
function findRow(performance, metric) {
  const rows = performance && performance.rows;
  if (!Array.isArray(rows)) return undefined;
  return rows.find((r) => r && r.metric === metric);
}

// lastForecastPoint - the LAST (furthest-out) forecast-side BandPoint from a figures/forecast-
// band response, i.e. the end-of-horizon projection - the single headline number a board reader
// would want ("where do we land"), rather than the first forecast day (which is nearly identical
// to today's actual). Points are already oldest->newest (BuildForecastBand sorts them), so a
// reverse scan for the last is_forecast point is enough.
function lastForecastPoint(forecastBand) {
  const points = forecastBand && forecastBand.points;
  if (!Array.isArray(points)) return undefined;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    if (points[i] && points[i].is_forecast) return points[i];
  }
  return undefined;
}

// topDriver - the figures/pvm driver with the largest |delta| (favorable and adverse deltas
// compete on magnitude, not sign - a -€20k saving is just as "top" a story as a +€19k overage).
// `drivers` is already sorted by |delta| desc server-side (DecomposePVM), but this re-derives it
// defensively so resolveMetrics doesn't depend on that ordering holding forever.
function topDriver(pvm) {
  const drivers = pvm && pvm.drivers;
  if (!Array.isArray(drivers) || drivers.length === 0) return undefined;
  return drivers.reduce((best, d) => {
    if (!d || typeof d.delta !== "number") return best;
    if (!best) return d;
    return Math.abs(d.delta) > Math.abs(best.delta) ? d : best;
  }, undefined);
}

// identity - format fn for metrics whose raw value is already display-ready text (e.g. a driver
// name), so resolveMetrics's "format the raw value" step still has something to call.
function identity(v) {
  return v;
}

// METRIC_CATALOG - the ~14 core report metrics @figure tokens can reference. Each entry:
//   key      - the {{fig:KEY}} token identifier
//   label    - human label (for the @-mention picker / source-data panels, later SP6 tasks)
//   group    - a loose grouping for the picker UI ("kpis" | "forecast" | "risk" | "pvm")
//   endpoint - the figure endpoint this metric's data comes from, RELATIVE to
//              /board-reports/{id}/runs/{runId}/ (e.g. "figures/kpis") - useReportMetrics.js
//              fetches each DISTINCT endpoint exactly once
//   select(data) - reads the raw value from that endpoint's UNWRAPPED response; may return
//              undefined when the field isn't present - resolveMetrics treats that as "—"
//   format(raw)  - renders the raw value for display (EUR / %); only called when select
//              returned a non-null value
export const METRIC_CATALOG = [
  {
    key: "total_spend",
    label: "Total spend (month-to-date)",
    group: "kpis",
    endpoint: "figures/kpis",
    select: (data) => findMeasure(data, "month_spend")?.value,
    format: humanEUR,
  },
  {
    key: "cloud_total",
    label: "Cloud spend",
    group: "kpis",
    endpoint: "figures/performance",
    select: (data) => findRow(data, "Cloud")?.month,
    format: humanEUR,
  },
  {
    key: "ai_total",
    label: "AI spend",
    group: "kpis",
    endpoint: "figures/performance",
    select: (data) => findRow(data, "AI")?.month,
    format: humanEUR,
  },
  {
    key: "cloud_pct",
    label: "Cloud share of total",
    group: "kpis",
    endpoint: "figures/performance",
    select: (data) => {
      const cloud = findRow(data, "Cloud")?.month;
      const total = findRow(data, "Total")?.month;
      if (cloud == null || !total) return undefined;
      return cloud / total;
    },
    format: pct,
  },
  {
    key: "ai_pct",
    label: "AI share of total",
    group: "kpis",
    endpoint: "figures/performance",
    select: (data) => {
      const ai = findRow(data, "AI")?.month;
      const total = findRow(data, "Total")?.month;
      if (ai == null || !total) return undefined;
      return ai / total;
    },
    format: pct,
  },
  {
    key: "mom_pct",
    label: "Month-over-month change",
    group: "kpis",
    endpoint: "figures/kpis",
    select: (data) => findMeasure(data, "mom")?.value,
    format: pct,
  },
  {
    key: "prev_month_total",
    label: "Prior month spend",
    group: "kpis",
    endpoint: "figures/kpis",
    // Derived, not a raw field: mom is MoMPct = (actual - prev) / prev (kpi.go's
    // spendKPI.MoMPct), so prev = actual / (1 + mom). Both month_spend (Actual) and mom (the
    // raw fraction, NOT the pre-formatted Delta string) live in the SAME /figures/kpis
    // measures array already fetched for total_spend/mom_pct, so this needs no new endpoint.
    // Guards: missing month_spend measure, non-numeric mom, or the degenerate 1 + mom === 0
    // (would make the division undefined/Infinity) all fall back to undefined -> "—".
    select: (data) => {
      const actual = findMeasure(data, "month_spend")?.value;
      const mom = findMeasure(data, "mom")?.value;
      if (typeof actual !== "number" || typeof mom !== "number") return undefined;
      const denom = 1 + mom;
      if (denom === 0) return undefined;
      return actual / denom;
    },
    format: humanEUR,
  },
  {
    key: "forecast_midpoint",
    label: "Forecast (end of horizon)",
    group: "forecast",
    endpoint: "figures/forecast-band",
    select: (data) => lastForecastPoint(data)?.forecast,
    format: humanEUR,
  },
  {
    key: "forecast_low",
    label: "Forecast — low (90% range)",
    group: "forecast",
    endpoint: "figures/forecast-band",
    select: (data) => lastForecastPoint(data)?.lower,
    format: humanEUR,
  },
  {
    key: "forecast_high",
    label: "Forecast — high (90% range)",
    group: "forecast",
    endpoint: "figures/forecast-band",
    select: (data) => lastForecastPoint(data)?.upper,
    format: humanEUR,
  },
  {
    key: "cap",
    label: "Budget cap",
    group: "forecast",
    endpoint: "figures/forecast-band",
    // Rebound from figures/risk-bridge's upper_total (that's base + Σlegs, a risk-adjusted
    // forecast CEILING, not a budget limit - see the git history of this entry for the prior,
    // mislabeled binding). BandPoint DOES carry a real budget/cap line (`json:"budget"` -
    // forecast.go's BuildForecastBand, sourced from boardFigureBudgetByDate: the month's budget
    // repeated across every day) - use the same last-forecast-point the forecast_* metrics
    // already read, so "cap" means the actual month-end budget/cap a board reader expects.
    select: (data) => lastForecastPoint(data)?.budget,
    format: humanEUR,
  },
  // breach_prob (a modeled probability of breaching the cap) was dropped from the catalog:
  // no probability/likelihood field exists on any of the 5 figure endpoints Task 1 fetches -
  // it only lives on the financial-control summary (a DIFFERENT endpoint, not fetched here,
  // and one that needs the run's period threaded through to query). Deferred until a
  // financial-control-summary metric source is wired up for board reports; offering a
  // catalog entry that always resolves "—" would be a dead metric authors could still pick.
  {
    key: "top_driver_name",
    label: "Top driver",
    group: "pvm",
    endpoint: "figures/pvm",
    select: (data) => topDriver(data)?.driver,
    format: identity,
  },
  {
    key: "top_driver_delta",
    label: "Top driver's move (Δ)",
    group: "pvm",
    endpoint: "figures/pvm",
    select: (data) => topDriver(data)?.delta,
    format: humanEUR,
  },
];

// resolveMetrics - pure: never throws, regardless of missing/malformed input. figuresByEndpoint
// is `{ [endpoint]: <unwrapped figure data> }` (useReportMetrics.js builds this from useBlockData
// + each endpoint's envelope key). A metric whose endpoint entry is absent, or whose select()
// returns null/undefined/NaN, resolves to `{ value: "—" }` - the same "no data yet" contract
// renderNarrative.jsx (a later SP6 task) uses for an unresolved {{fig:KEY}} token.
export function resolveMetrics(figuresByEndpoint) {
  const result = {};
  METRIC_CATALOG.forEach((metric) => {
    const { key, label } = metric;
    let value = "—";
    try {
      const data = figuresByEndpoint ? figuresByEndpoint[metric.endpoint] : undefined;
      if (data !== undefined && data !== null) {
        const raw = metric.select(data);
        const isMissing = raw === undefined || raw === null || (typeof raw === "number" && Number.isNaN(raw));
        if (!isMissing) {
          value = metric.format(raw);
        }
      }
    } catch {
      value = "—";
    }
    result[key] = { key, label, value };
  });
  return result;
}
